import { useEffect, useMemo, useState } from "react";
import { CheckCircle, XCircle, ArrowCounterClockwise, Trophy } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { shuffleQuiz } from "@/data/quizzes";

function QuizQuestion({ q, n, chosen, onChoose, es, testPrefix }) {
  const answered = chosen !== undefined;
  const correct = chosen === q.a;
  return (
    <div className="bg-[#121214]/80 border border-white/10 rounded-xl p-4 sm:p-5" data-testid={`${testPrefix}-q-${n}`}>
      <p className="text-white font-medium text-sm sm:text-base mb-3">
        <span className="font-['JetBrains_Mono'] text-[#F5A524] mr-2">{n + 1}.</span>
        {q.q}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {q.o.map((opt, i) => {
          const isRight = answered && i === q.a;
          const isWrong = answered && i === chosen && !correct;
          return (
            <button
              key={opt}
              type="button"
              disabled={answered}
              onClick={() => onChoose(i)}
              data-testid={`${testPrefix}-q-${n}-opt-${i}`}
              className={`text-left text-sm rounded-lg border px-3 py-2.5 transition-colors ${
                isRight
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                  : isWrong
                  ? "border-red-500/60 bg-red-500/10 text-red-300"
                  : answered
                  ? "border-white/5 text-[#52525B]"
                  : "border-white/10 text-[#D4D4D8] hover:border-[#F5A524]/50 hover:bg-[#F5A524]/5"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {answered && (
        <div
          className={`mt-3 flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${correct ? "bg-emerald-500/5 text-emerald-200" : "bg-red-500/5 text-red-200"}`}
          data-testid={`${testPrefix}-q-${n}-explanation`}
        >
          {correct ? <CheckCircle weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" /> : <XCircle weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-red-400" />}
          <span>
            <strong>{correct ? (es ? "¡Correcto! " : "Correct! ") : es ? "No exactamente. " : "Not quite. "}</strong>
            <span className="text-[#A1A1AA]">{q.w}</span>
          </span>
        </div>
      )}
    </div>
  );
}

export default function LessonQuiz({ questions, testPrefix = "lesson-quiz", onFinish, allowRetry = true }) {
  const { lang } = useTranslation();
  const es = lang === "es";
  const [attempt, setAttempt] = useState(0);
  const [answers, setAnswers] = useState({});
  const qs = useMemo(() => shuffleQuiz(questions), [questions, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = Object.keys(answers).length;
  const score = qs.filter((q, i) => answers[i] === q.a).length;
  const finished = done === qs.length;

  useEffect(() => {
    if (finished && onFinish) onFinish(score, qs.length, qs.map((q, i) => i).filter((i) => answers[i] !== qs[i].a));
  }, [finished]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = () => {
    setAnswers({});
    setAttempt((a) => a + 1);
  };

  return (
    <div className="space-y-3" data-testid={testPrefix}>
      {qs.map((q, i) => (
        <QuizQuestion key={`${attempt}-${i}`} q={q} n={i} es={es} testPrefix={testPrefix} chosen={answers[i]} onChoose={(c) => setAnswers((a) => ({ ...a, [i]: c }))} />
      ))}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3">
        {finished ? (
          <p className="flex items-center gap-2 text-white text-sm sm:text-base" data-testid={`${testPrefix}-score`}>
            <Trophy weight="fill" className="w-5 h-5 text-[#F5A524]" />
            {es ? "Tu puntaje: " : "Your score: "}
            <span className="font-['JetBrains_Mono'] text-[#F5A524]">{score}/{qs.length}</span>
            <span className="text-[#71717A] text-sm">
              {score === qs.length ? (es ? "· ¡Perfecto!" : "· Perfect!") : score >= qs.length - 1 ? (es ? "· ¡Muy bien!" : "· Great job!") : es ? "· Repasa la lección e inténtalo otra vez" : "· Review the lesson and try again"}
            </span>
          </p>
        ) : (
          <p className="text-[#71717A] text-sm" data-testid={`${testPrefix}-progress`}>
            {es ? "Respondidas" : "Answered"} <span className="font-['JetBrains_Mono'] text-white">{done}/{qs.length}</span>
          </p>
        )}
        {done > 0 && allowRetry && (
          <Button variant="outline" size="sm" onClick={retry} className="border-white/15 text-[#D4D4D8] hover:bg-white/5" data-testid={`${testPrefix}-retry`}>
            <ArrowCounterClockwise weight="bold" className="w-4 h-4 mr-1.5" />
            {es ? "Intentar de nuevo" : "Try again"}
          </Button>
        )}
      </div>
    </div>
  );
}
